import { SearchOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { Box, InputAdornment, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type SystemChoiceItem, SystemChoiceList } from "../../../../../components/UIToolkit/Clickable/SystemChoiceList";
import { filterSearchFieldProps } from "../../../../../components/UIToolkit/Inputs/filterSearchFieldProps";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { BrandMarkTile } from "../../../../../components/UIToolkit/molecules/BrandMarkTile";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import { FONT_SIZE, INK, STATUS } from "../../../../../theme/tokens";
import type { ExternalSource } from "../../../../../utils/models";
import { HRIS_SOURCES, hrisSourceTileColors } from "../generalMetadataConsts";

type Props = {
	open: boolean;
	/** Sources already configured for this department — offered again would create a duplicate card. */
	usedSources: ExternalSource[];
	/**
	 * `source → file_based`, from the backend's syncher registry. `undefined` when the lookup failed —
	 * distinct from an empty map, so a failed request doesn't label every source "needs engineering".
	 */
	fileBasedBySource: Map<ExternalSource, boolean> | undefined;
	onClose: () => void;
	onPick: (source: ExternalSource) => void;
};

/**
 * Picks the HRIS source to add, and says up front which ones ops can finish on its own.
 *
 * File-based sources need nothing but an SFTP folder and a filename, both of which this page can now
 * provide. API sources additionally need a key, tenant or permission grant that only engineering can
 * provision, so they are offered but flagged — the alternative (hiding them) would leave an operator
 * with no way to see that the integration exists at all.
 *
 * Each row leads with the vendor's `BrandMarkTile`, so seventeen rows of similar-looking text become
 * seventeen shapes — which matters most for the three Pay* vendors the eye cannot separate by name.
 */
export const AddHrisSourceDialog: FC<Props> = ({ open, usedSources, fileBasedBySource, onClose, onPick }) => {
	const { t } = useTranslation();
	const [searchQuery, setSearchQuery] = useState("");
	const available = HRIS_SOURCES.filter(({ value }) => !usedSources.includes(value));
	const filtered = available.filter((s) => s.label.toLowerCase().includes(searchQuery.trim().toLowerCase()));

	// Reset search when dialog opens
	useEffect(() => {
		if (open) {
			setSearchQuery("");
		}
	}, [open]);

	const items: SystemChoiceItem[] = filtered.map(({ value, label, mark }) => {
		// Unknown while the request is in flight; also unknown if it failed. Only a definite `false`
		// earns the "needs engineering" label — guessing it would send an operator to escalate a
		// source they can configure right here.
		const fileBased = fileBasedBySource?.get(value);
		const tileColors = hrisSourceTileColors(value);
		return {
			value,
			label,
			dataTestId: `hris-add-source-option-${value}`,
			// Phrasing content only — the choice row is a `<button>`.
			content: (
				<Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
					<BrandMarkTile
						name={label}
						mark={mark}
						size="sm"
						component="span"
						color={tileColors?.color}
						tint={tileColors?.tint}
						dataTestId={`hris-source-mark-${value}`}
					/>
					<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
						<Box component="span" sx={{ color: INK.PRIMARY }}>
							{label}
						</Box>
						{fileBased === true && (
							<Box component="span" sx={{ fontSize: FONT_SIZE.CAPTION, fontWeight: 500, color: INK.MUTED }}>
								{t("department.config.integrations.hris.addSource.fileBased")}
							</Box>
						)}
						{fileBased === false && (
							<Box
								component="span"
								sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: FONT_SIZE.CAPTION, fontWeight: 500, color: STATUS.WARNING.main }}
							>
								<WarningAmberOutlined sx={{ fontSize: 14, flexShrink: 0 }} />
								{t("department.config.integrations.hris.addSource.needsEngineering")}
							</Box>
						)}
					</Box>
				</Box>
			),
		};
	});

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			title={t("department.config.integrations.hris.addSource.title")}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
					<SystemBanner
						tone="info"
						title={t("department.config.integrations.hris.addSource.hint.title")}
						description={t("department.config.integrations.hris.addSource.hint.description")}
						dataTestId="hris-add-source-hint"
					/>
					{available.length === 0 ? (
						<Typography variant="body2" sx={{ color: INK.MUTED }}>
							{t("department.config.integrations.hris.addSource.allUsed")}
						</Typography>
					) : (
						<>
							<SystemValidationTextField
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								onClear={searchQuery ? () => setSearchQuery("") : undefined}
								placeholder={t("department.config.integrations.hris.addSource.search")}
								autoFocus
								fullWidth
								// The field sits directly above the option rows, so a reserved helper-text line would
								// open a gap between the search and the list it filters.
								reserveHeightForHelperText={false}
								{...filterSearchFieldProps("hris-source-search")}
								inputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchOutlined fontSize="small" />
										</InputAdornment>
									),
								}}
								dataTestId="hris-source-search"
							/>
							{items.length === 0 ? (
								<Typography variant="body2" sx={{ color: INK.MUTED }}>
									{t("department.config.integrations.hris.addSource.noResults")}
								</Typography>
							) : (
								<SystemChoiceList
									items={items}
									onSelect={(value) => onPick(value as ExternalSource)}
									ariaLabel={t("department.config.integrations.hris.addSource.title")}
									dataTestId="hris-add-source-options"
								/>
							)}
						</>
					)}
				</Box>
			}
			// The list rows ARE the action, so the footer holds only a way out — one Close, not a
			// Cancel/Confirm pair that would imply a second selection step.
			hideCancel
			confirm={t("close")}
			onConfirm={onClose}
			dataTestId="hris-add-source-dialog"
		/>
	);
};
